/**
 * Wandering trader (vanilla Java): 20 HP, 0.6 x 1.95, passive. Right-click
 * opens the trade screen (six emerald offers, no restock, see trades.ts).
 *  - Strolls around its wander target (the village meeting point when one was
 *    near, else where it spawned) and watches players.
 *  - Flees zombies (8 blocks), vindicators (8) and pillagers (15), and panics
 *    for 5 s when hit; a hit by the player also makes its trader llamas spit.
 *  - Once it is dark it drinks a Potion of Invisibility (32 ticks, 3 min of
 *    invisibility) and in daylight a milk bucket to show itself again. It only
 *    drops the bottle / bucket when it dies mid-drink, never anything else.
 *  - Despawns 48000 ticks (40 min) after it appeared, together with its
 *    llamas; the countdown pauses while a player is trading with it.
 * `view.invisible` hides the model; the despawn timer, offers and
 * invisibility persist through saveExtra / loadExtra.
 */

import { I } from '../../game/itemIds';
import { potionStack } from '../../game/brewing/potions';
import { offerFromRow, offerToRow, type Offer } from '../../game/trading';
import { stackOf, type Stack } from '../../game/types';
import type { EntityHost } from '../host';
import { Mob, type MobCtx } from '../mob';
import { playSound, traderHooks, vanishedTraders } from './hooks';
import { rollTraderOffers } from './trades';

export const TRADER_DESPAWN = 48000;
export const DRINK_TICKS = 32;
/** Potion of Invisibility: 3:00. */
export const INVIS_TICKS = 3600;
const PANIC_TICKS = 100;
/** Mobs the trader runs from and how close they may come (blocks). */
export const TRADER_THREATS: ReadonlyMap<string, number> = new Map([
  ['zombie', 8], ['zombie_villager', 8], ['husk', 8], ['drowned', 8], ['vindicator', 8], ['pillager', 15],
]);

export class WanderingTrader extends Mob {
  /** Stable identity (ids are re-allocated on load): the trader llamas link to it. */
  uid: number;
  offers: Offer[];
  xp = 0;
  /** A player has the trade screen open. */
  trading = false;
  /** Ticks left of the despawn countdown. */
  despawn = TRADER_DESPAWN;
  /** Ticks left drinking; the held item shows what. */
  drinking = 0;
  private drinkKind: 'potion' | 'milk' | '' = '';
  invisTicks = 0;
  /** Wander target (x, z): the trader strolls within 16 blocks of it. */
  anchor: [number, number] | null = null;
  /** Ticks since the player last hit it (its llamas defend it), and the last mob that hit it. */
  hurtByPlayer = 0;
  attacker: Mob | null = null;
  private fleeFrom: Mob | null = null;
  private scanCd: number;

  readonly title = 'Wandering Trader';

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'wandering_trader', x, y, z, { width: 0.6, height: 1.95, eye: 1.62, health: 20, speed: 0.5 }, rnd);
    this.uid = 1 + Math.floor(rnd() * 0x3fffffff);
    this.offers = rollTraderOffers(rnd);
    this.scanCd = Math.floor(rnd() * 10);
  }

  get invisible(): boolean { return this.invisTicks > 0; }

  xpValue(): number { return 0; }

  /** Dying mid-drink drops the bottle or bucket in hand; nothing otherwise. */
  loot(): Stack[] {
    if (this.drinking <= 0) return [];
    return this.drinkKind === 'milk' ? [stackOf(I.MILK_BUCKET, 1)] : [potionStack('invisibility')];
  }

  override onHurtBy(): void {
    this.panicTicks = PANIC_TICKS;
    this.hurtByPlayer = 200;
    playSound('entity.wandering_trader.hurt', this);
  }
  /** Another mob hit it (spit, zombie): flee and let the llamas deal with the attacker. */
  onHurtByMob(attacker: Mob): void { this.panicTicks = PANIC_TICKS; this.attacker = attacker; }

  /** The glue has the player in the trade screen / has closed it. */
  onTradeClosed(): void { this.trading = false; }

  /** Poof out of the world; its llamas notice through `vanishedTraders`. */
  vanish(): void {
    if (this.removed) return;
    this.removed = true;
    vanishedTraders.add(this.uid);
    this.host?.effect('poof', this.x, this.y + this.height / 2, this.z);
  }

  /** Despawn countdown for a trader too far from the player to be simulated. */
  tickFar(): void {
    if (this.removed || this.trading) return;
    if (this.despawn > 0 && --this.despawn === 0) { this.removed = true; vanishedTraders.add(this.uid); }
  }

  private nearThreat(range: number): Mob | null {
    let best: Mob | null = null, bd = Infinity;
    for (const m of traderHooks.mobs()) {
      if (m === this || !m.alive) continue;
      const r = TRADER_THREATS.get(m.type);
      if (r === undefined) continue;
      const dx = m.x - this.x, dz = m.z - this.z, d = dx * dx + dz * dz;
      if (d < r * r && d < bd && Math.abs(dx) < range && Math.abs(dz) < range) { bd = d; best = m; }
    }
    return best;
  }

  protected ai(ctx: MobCtx): void {
    const p = ctx.host.player;
    if (this.hurtByPlayer > 0) this.hurtByPlayer--;
    if (this.invisTicks > 0) this.invisTicks--;
    if (this.attacker && !this.attacker.alive) this.attacker = null;
    if (!this.trading && this.despawn > 0 && --this.despawn === 0) { this.vanish(); return; }

    if (this.trading) {
      this.lookAt(p.x, p.eyeY, p.z);
      this.faceToward(p.x, p.z);
      return;
    }
    if (this.drinking > 0) {
      if (--this.drinking === 0) this.finishDrink();
      return;
    }
    if (--this.scanCd <= 0) { this.scanCd = 10; this.fleeFrom = this.nearThreat(16); }
    if (this.fleeFrom && !this.fleeFrom.alive) this.fleeFrom = null;
    if (this.fleeFrom || this.panicTicks > 0) {
      if (this.panicTicks > 0) this.panicTicks--;
      const from = this.fleeFrom ?? this.attacker ?? { x: p.x, z: p.z };
      if (!this.navAway(ctx, from.x, from.z, 0.9, 3, true)) {
        const dx = this.x - from.x, dz = this.z - from.z;
        if (this.safeAhead(dx, dz, 3, true)) this.setMove(dx, dz, 0.9);
      }
      return;
    }

    // UseItemGoal: potion once it is dark, milk once it is bright again
    const night = ctx.host.isNight();
    if (night && this.invisTicks <= 0) { this.startDrink('potion'); return; }
    if (!night && this.invisTicks > 0) { this.startDrink('milk'); return; }

    const a = this.anchor;
    if (a) {
      this.leashHome = { x: a[0], z: a[1], r: 16 };
      const dx = a[0] - this.x, dz = a[1] - this.z;
      if (dx * dx + dz * dz > 20 * 20) {
        if (this.navTo(ctx, a[0], this.y, a[1], 0.7, 3, true, 48, 400)) return;
      }
    }
    this.stroll(ctx, 0.7, 3, true);
    this.lookAtNearPlayer(ctx);
  }

  private startDrink(kind: 'potion' | 'milk'): void {
    this.drinkKind = kind;
    this.drinking = DRINK_TICKS;
    this.heldItem = kind === 'potion' ? I.POTION : I.MILK_BUCKET;
    playSound(kind === 'potion' ? 'entity.wandering_trader.drink_potion' : 'entity.wandering_trader.drink_milk', this);
  }

  private finishDrink(): void {
    if (this.drinkKind === 'potion') {
      this.invisTicks = INVIS_TICKS;
      playSound('entity.wandering_trader.disappeared', this);
    } else {
      this.invisTicks = 0;
      playSound('entity.wandering_trader.reappeared', this);
    }
    this.drinkKind = '';
    this.heldItem = 0;
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.invisible = this.invisTicks > 0;
    this.view.baby = false;
    this.swell = 0;
  }

  saveExtra(): unknown {
    return {
      u: this.uid, o: this.offers.map(offerToRow), x: this.xp, d: this.despawn, iv: this.invisTicks,
      dr: this.drinking, dk: this.drinkKind === 'milk' ? 2 : this.drinkKind === 'potion' ? 1 : 0, an: this.anchor,
    };
  }

  loadExtra(d: unknown): void {
    const o = d as Record<string, unknown>;
    if (!o || typeof o !== 'object') return;
    const n = (v: unknown, def = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : def);
    if (n(o.u) > 0) this.uid = Math.floor(n(o.u));
    if (Array.isArray(o.o)) {
      const rows = (o.o as unknown[]).map(offerFromRow).filter((x): x is Offer => x !== null);
      if (rows.length) this.offers = rows;
    }
    this.xp = n(o.x);
    this.despawn = Math.max(1, Math.min(TRADER_DESPAWN, n(o.d, TRADER_DESPAWN)));
    this.invisTicks = Math.max(0, n(o.iv));
    this.drinking = Math.max(0, n(o.dr));
    this.drinkKind = n(o.dk) === 2 ? 'milk' : n(o.dk) === 1 ? 'potion' : '';
    if (this.drinking > 0 && !this.drinkKind) this.drinking = 0;
    this.heldItem = this.drinking > 0 ? (this.drinkKind === 'milk' ? I.MILK_BUCKET : I.POTION) : 0;
    const a = o.an;
    this.anchor = Array.isArray(a) && a.length === 2 ? [n(a[0]), n(a[1])] : null;
  }
}
